/**
 * @file ContactForm - Formulaire de contact multi-étapes
 * @description Formulaire de contact avec validation en temps réel,
 *              navigation par étapes et intégration reCAPTCHA.
 *              Étape 1 : Informations personnelles (nom, email/téléphone)
 *              Étape 2 : Message et conditions d'utilisation
 */

/* =============================================================================
   CONFIGURATION
   ============================================================================= */

/** Durée d'affichage des erreurs principales (en millisecondes) */
const ERROR_DISPLAY_DURATION = 3000;

/** Patterns de validation des champs */
const VALIDATION_PATTERNS = {
  email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,
  phone: /^[+0-9][0-9 ]*[0-9]$/,
};

/** Sélecteurs CSS du composant */
const SELECTORS = {
  component: '.m-contactForm',
  form: 'form[name="contact_form"]',
  translations: 'input[data-type="translation"]',
  modal: 'contact-form-modal',
};

/** Clés de traduction pour les messages d'erreur */
const ERROR_KEYS = {
  required: 'contact.errorMessages.required',
  email: 'contact.errorMessages.email',
  phone: 'contact.errorMessages.phone',
  info: 'contact.errorMessages.info',
  terms: 'contact.errorMessages.terms',
  captcha: 'contact.errorMessages.captcha',
  main: 'contact.errorMessages.main',
};

/* =============================================================================
   UTILITAIRES DOM
   ============================================================================= */

/**
 * Définit le contenu textuel d'un élément de manière sécurisée (anti-XSS)
 * @param {HTMLElement|null} element - Élément cible
 * @param {string|number} text - Texte à afficher
 */
const setText = (element, text) => {
  if (element) {
    element.textContent = text;
  }
};

/**
 * Active ou désactive un bouton avec les classes et attributs appropriés
 * @param {HTMLButtonElement|null} button - Bouton cible
 * @param {boolean} disabled - État désactivé souhaité
 */
const setButtonDisabled = (button, disabled) => {
  if (!button) {
    return;
  }

  button.classList.toggle('-disabled', disabled);
  button.toggleAttribute('disabled', disabled);
};

/**
 * Récupère un élément d'erreur associé à un champ
 * @param {string} fieldId - ID du champ
 * @returns {HTMLElement|null} Élément d'erreur
 */
const getErrorElement = (fieldId) => document.getElementById(`${fieldId}-error`);

/* =============================================================================
   TRADUCTIONS
   ============================================================================= */

/**
 * Charge les traductions depuis les inputs cachés du DOM
 * @returns {Record<string, string>} Dictionnaire clé/valeur des traductions
 */
const loadTranslations = () => Object.fromEntries(
  [...document.querySelectorAll(SELECTORS.translations)]
    .map((el) => [el.dataset.key, el.dataset.value])
    .filter(([key, value]) => key && value),
);

/* =============================================================================
   VALIDATION DES CHAMPS
   ============================================================================= */

/**
 * Vérifie si un champ respecte la contrainte HTML5 required
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field - Champ à valider
 * @returns {boolean} true si valide
 */
const isFieldRequired = (field) => field.validity.valid;

/**
 * Vérifie si une valeur correspond à un pattern regex
 * @param {string} value - Valeur à tester
 * @param {RegExp} pattern - Expression régulière
 * @returns {boolean} true si valide
 */
const matchesPattern = (value, pattern) => pattern.test(value);

/**
 * Vérifie si une checkbox est cochée
 * @param {HTMLInputElement} field - Checkbox à vérifier
 * @returns {boolean} true si cochée
 */
const isChecked = (field) => field.checked;

/**
 * Extrait le nom du champ depuis l'attribut name du formulaire
 * @param {string} fieldName - Nom complet (ex: "contact_form[lastname]")
 * @returns {string} Nom simplifié (ex: "lastname")
 */
const extractFieldName = (fieldName) => fieldName.replace('contact_form[', '').replace(']', '');

/**
 * Génère le sélecteur CSS du modal pour un champ donné
 * @param {string} fieldName - Nom simplifié du champ
 * @returns {string} Sélecteur CSS (ex: ".js-contactForm-modalLastname")
 */
const getModalSelector = (fieldName) => `.js-contactForm-modal${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;

/**
 * Met à jour l'élément modal avec la valeur du champ
 * @param {HTMLInputElement} field - Champ source
 */
const updateModalValue = (field) => {
  const fieldName = extractFieldName(field.name);
  const modalElement = document.querySelector(getModalSelector(fieldName));

  if (modalElement && field.value) {
    setText(modalElement, field.value);
  }
};

/**
 * Valide un champ requis et affiche l'erreur si nécessaire
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field - Champ à valider
 * @param {Record<string, string>} translations - Traductions
 * @param {boolean} showError - Afficher l'erreur
 * @returns {boolean} true si valide
 */
const validateRequired = (field, translations, showError) => {
  const isValid = isFieldRequired(field);
  const errorElement = getErrorElement(field.id);

  if (!isValid && showError) {
    setText(errorElement, translations[ERROR_KEYS.required]);
  } else if (isValid) {
    setText(errorElement, '');
    updateModalValue(field);
  }

  return isValid;
};

/**
 * Valide un champ avec un pattern et affiche l'erreur si nécessaire
 * @param {HTMLInputElement} field - Champ à valider
 * @param {RegExp} pattern - Pattern de validation
 * @param {string} errorKey - Clé de l'erreur
 * @param {Record<string, string>} translations - Traductions
 * @param {boolean} showError - Afficher l'erreur
 * @returns {boolean} true si valide
 */
const validatePattern = (field, pattern, errorKey, translations, showError) => {
  const isValid = !field.value || matchesPattern(field.value, pattern);
  const errorElement = getErrorElement(field.id);

  if (!isValid && showError) {
    setText(errorElement, translations[errorKey]);
  } else if (isValid) {
    setText(errorElement, '');
    updateModalValue(field);
  }

  return isValid;
};

/**
 * Valide une checkbox et affiche l'erreur si nécessaire
 * @param {HTMLInputElement} field - Checkbox à valider
 * @param {string} errorKey - Clé de l'erreur
 * @param {Record<string, string>} translations - Traductions
 * @param {boolean} showError - Afficher l'erreur
 * @returns {boolean} true si valide
 */
const validateCheckbox = (field, errorKey, translations, showError) => {
  const isValid = isChecked(field);
  const errorElement = getErrorElement(field.id);

  if (!isValid && showError) {
    setText(errorElement, translations[errorKey]);
  } else if (isValid) {
    setText(errorElement, '');
  }

  return isValid;
};

/* =============================================================================
   GESTION DES ÉTAPES
   ============================================================================= */

/**
 * Change l'étape du formulaire et met à jour l'affichage
 * @param {HTMLFormElement} form - Élément formulaire
 * @param {HTMLElement|null} stepIndexElement - Élément affichant le numéro d'étape
 * @param {number} fromStep - Étape actuelle
 * @param {number} toStep - Nouvelle étape
 */
const changeStep = (form, stepIndexElement, fromStep, toStep) => {
  form.classList.replace(`m-contactForm__step-${fromStep}`, `m-contactForm__step-${toStep}`);
  setText(stepIndexElement, toStep);
};

/* =============================================================================
   VALIDATION COMPLÈTE DU FORMULAIRE
   ============================================================================= */

/**
 * Détermine si les erreurs doivent être affichées pour une étape donnée
 * @param {boolean} showErrors - Flag d'affichage global
 * @param {number} fieldStep - Étape du champ
 * @param {number} currentStep - Étape actuelle
 * @param {boolean} isFullMode - Mode formulaire complet
 * @returns {boolean} true si les erreurs doivent être affichées
 */
const shouldShowError = (showErrors, fieldStep, currentStep, isFullMode) =>
  showErrors && (fieldStep === currentStep || isFullMode);

/**
 * Valide l'étape 1 du formulaire (informations personnelles)
 * @param {Object} fields - Références aux champs
 * @param {Record<string, string>} translations - Traductions
 * @param {boolean} showErrors - Afficher les erreurs
 * @param {boolean} isFullMode - Mode formulaire complet
 * @returns {boolean} true si l'étape 1 est valide
 */
const validateStep1 = (fields, translations, showErrors, isFullMode) => {
  const showStep1Error = shouldShowError(showErrors, 1, 1, isFullMode);

  // Validation des champs requis
  const subjectValid = validateRequired(fields.subject, translations, showStep1Error);
  const lastnameValid = validateRequired(fields.lastname, translations, showStep1Error);
  const firstnameValid = validateRequired(fields.firstname, translations, showStep1Error);

  // Validation email/téléphone (au moins un requis)
  const hasContactInfo = Boolean(fields.email.value || fields.phone.value);
  const infoErrorElement = document.getElementById('info-error');

  let emailValid = true;
  let phoneValid = true;

  if (!hasContactInfo) {
    if (showStep1Error) {
      setText(infoErrorElement, translations[ERROR_KEYS.info]);
    }

    setText(getErrorElement(fields.email.id), '');
    setText(getErrorElement(fields.phone.id), '');
  } else {
    setText(infoErrorElement, '');

    if (fields.email.value) {
      emailValid = validatePattern(fields.email, VALIDATION_PATTERNS.email, ERROR_KEYS.email, translations, showStep1Error);
    }

    if (fields.phone.value) {
      phoneValid = validatePattern(fields.phone, VALIDATION_PATTERNS.phone, ERROR_KEYS.phone, translations, showStep1Error);
    }
  }

  return subjectValid && lastnameValid && firstnameValid && hasContactInfo && emailValid && phoneValid;
};

/**
 * Valide l'étape 2 du formulaire (message et conditions)
 * @param {Object} fields - Références aux champs
 * @param {Record<string, string>} translations - Traductions
 * @param {boolean} showErrors - Afficher les erreurs
 * @param {number} currentStep - Étape actuelle
 * @param {boolean} isFullMode - Mode formulaire complet
 * @param {boolean} captchaOk - État du captcha
 * @returns {boolean} true si l'étape 2 est valide
 */
const validateStep2 = (fields, translations, showErrors, currentStep, isFullMode, captchaOk) => {
  const showStep2Error = shouldShowError(showErrors, 2, currentStep, isFullMode);

  // Validation message et conditions
  const messageValid = validateRequired(fields.message, translations, showStep2Error);
  const termsValid = validateCheckbox(fields.terms, ERROR_KEYS.terms, translations, showStep2Error);

  // Validation reCAPTCHA
  const recaptchaErrorElement = document.getElementById('recaptcha-error');

  if (!captchaOk && showStep2Error) {
    setText(recaptchaErrorElement, translations[ERROR_KEYS.captcha]);
  } else if (captchaOk) {
    setText(recaptchaErrorElement, '');
  }

  return messageValid && termsValid && captchaOk;
};

/* =============================================================================
   SOUMISSION DU FORMULAIRE
   ============================================================================= */

/**
 * Affiche une erreur temporaire dans le formulaire
 * @param {HTMLElement|null} errorElement - Élément d'erreur
 * @param {string} message - Message à afficher
 */
const showTemporaryError = (errorElement, message) => {
  if (!errorElement) {
    return;
  }

  setText(errorElement, message);
  errorElement.style.display = 'block';

  setTimeout(() => {
    setText(errorElement, '');
    errorElement.style.display = 'none';
  }, ERROR_DISPLAY_DURATION);
};

/**
 * Réinitialise tous les champs du formulaire
 * @param {Object} fields - Références aux champs
 */
const resetFields = (fields) => {
  Object.values(fields).forEach((field) => {
    if (field.type === 'checkbox') {
      field.checked = false;
    } else {
      field.value = '';
    }
  });

  window.grecaptcha?.reset();
};

/**
 * Envoie les données du formulaire au serveur
 * @param {string} action - URL de soumission
 * @param {HTMLFormElement} form - Élément formulaire
 * @param {Function} onSuccess - Callback en cas de succès
 * @param {Function} onError - Callback en cas d'erreur
 */
const submitForm = async (action, form, onSuccess, onError) => {
  try {
    const response = await fetch(action, {
      method: 'POST',
      body: new FormData(form),
    });

    if (response.ok) {
      onSuccess();
    } else {
      onError();
    }
  } catch (error) {
    console.error('ContactForm submission error:', error);
    onError();
  }
};

/* =============================================================================
   INITIALISATION
   ============================================================================= */

/**
 * Initialise une instance du formulaire de contact
 * @param {HTMLFormElement} form - Élément formulaire à initialiser
 */
const initContactForm = (form) => {
  const action = form.action;
  const isFullMode = form.dataset.full === '1';
  const translations = loadTranslations();

  /* -------------------------------------------------------------------------
     État mutable
     ------------------------------------------------------------------------- */

  let currentStep = 1;
  let captchaOk = false;

  /* -------------------------------------------------------------------------
     Références aux champs du formulaire
     ------------------------------------------------------------------------- */

  const fields = {
    subject: form.elements['contact_form[subject]'],
    firstname: form.elements['contact_form[firstname]'],
    lastname: form.elements['contact_form[lastname]'],
    email: form.elements['contact_form[email]'],
    phone: form.elements['contact_form[phone]'],
    message: form.elements['contact_form[message]'],
    terms: form.elements['contact_form[terms]'],
  };

  /* -------------------------------------------------------------------------
     Références aux éléments UI
     ------------------------------------------------------------------------- */

  const component = document?.querySelector(SELECTORS.component);
  const nextBtn = form.querySelector('.js-contactForm-nextButton');
  const prevBtn = form.querySelector('.js-contactForm-prevButton');
  const submitBtn = form.querySelector('.js-contactForm-submitButton');
  const submitBtnXs = form.querySelector('.js-contactForm-submitButtonXs');
  const stepIndexElement = component?.querySelector('.js-contactForm-stepIndex');
  const mainErrorElement = form.querySelector('#contact-form-main-error');

  /* -------------------------------------------------------------------------
     Fonction de validation principale
     ------------------------------------------------------------------------- */

  const check = (showErrors = false, step = currentStep) => {
    const validStep1 = validateStep1(fields, translations, showErrors, isFullMode);
    const validStep2 = validateStep2(fields, translations, showErrors, step, isFullMode, captchaOk);
    const valid = validStep1 && validStep2;

    setButtonDisabled(nextBtn, !validStep1);
    setButtonDisabled(submitBtn, !valid);
    setButtonDisabled(submitBtnXs, !valid);

    return { validStep1, valid };
  };

  /* -------------------------------------------------------------------------
     Gestionnaires d'étapes
     ------------------------------------------------------------------------- */

  const goToStep2 = () => {
    changeStep(form, stepIndexElement, currentStep, 2);
    currentStep = 2;
  };

  const goToStep1 = () => {
    changeStep(form, stepIndexElement, currentStep, 1);
    currentStep = 1;
  };

  /* -------------------------------------------------------------------------
     Gestionnaire de soumission
     ------------------------------------------------------------------------- */

  const handleSubmitSuccess = () => {
    resetFields(fields);
    currentStep = 1;
    changeStep(form, stepIndexElement, 2, 1);
    check();
    document.getElementById(SELECTORS.modal)?.classList.add('-open');
  };

  const handleSubmitError = () => {
    showTemporaryError(mainErrorElement, translations[ERROR_KEYS.main]);
  };

  const handleSubmit = () => {
    submitForm(action, form, handleSubmitSuccess, handleSubmitError);
  };

  /* -------------------------------------------------------------------------
     Callbacks reCAPTCHA (exposés globalement)
     ------------------------------------------------------------------------- */

  window.recaptchaCallback = (token) => {
    captchaOk = token !== '';
    check();
  };

  window.recaptchaExpiredCallback = () => {
    captchaOk = false;
    check();
  };

  /* -------------------------------------------------------------------------
     Événements
     ------------------------------------------------------------------------- */

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    handleSubmit();
  });

  form.addEventListener('input', () => check());

  nextBtn?.addEventListener('click', goToStep2);
  nextBtn?.parentElement?.addEventListener('click', () => check(true, 1));

  prevBtn?.addEventListener('click', goToStep1);

  submitBtn?.parentElement?.addEventListener('click', () => check(true, 2));
  submitBtnXs?.parentElement?.addEventListener('click', () => check(true, 2));

  /* -------------------------------------------------------------------------
     Initialisation
     ------------------------------------------------------------------------- */

  check();
};

/* =============================================================================
   POINT D'ENTRÉE
   ============================================================================= */

/**
 * Point d'entrée du composant ContactForm
 * Recherche et initialise le formulaire de contact dans le DOM
 */
const ContactForm = () => {
  const form = document.querySelector(SELECTORS.form);

  if (form) {
    initContactForm(form);
  }
};

export default ContactForm;
